/**
 * Dynamic Modal Helper — canonical pattern for all form modals.
 *
 * Contract (see ``form-modals.mdc``):
 *   - Modal shell is static in the page; ``.modal-content`` is empty
 *     (or holds a spinner placeholder) until ``open(url, title)`` is
 *     called.
 *   - On ``open()``: GET ``url`` with ``X-Requested-With``, inject
 *     response HTML into ``.modal-content``, fire ``onContentInjected``.
 *   - Inner ``<form>`` submit is intercepted: POST as AJAX, on JSON
 *     ``{success: true}`` close + ``onSuccess(data)``, on HTML 4xx
 *     re-paint inline + fire ``onContentInjected``.
 *   - Submit button spinner via ``lockButton`` / ``unlockButton``.
 *   - Close paths: the static ``.modal-close-btn`` in the shell, any
 *     inner ``[data-modal-close]`` button, the Escape key (only when
 *     this modal is the visible one).
 *   - Backdrop click is **never** a dismiss path — half-filled forms
 *     must not be lost to a stray click.
 *
 * Sticky footer (PO 2026-05-21):
 *   - If the injected partial contains an element marked with
 *     ``data-modal-footer``, the helper HOISTS that element out of
 *     ``.modal-content`` and appends it as a direct sibling of the
 *     scroll body. The panel ``flex flex-col`` layout then renders it
 *     header + body + footer with the footer pinned at the bottom of
 *     the panel (always visible, even when the form is longer than
 *     the viewport). This mirrors the location-modal (hand-built
 *     static modal in ``customers/_form_content.html``) and matches
 *     industry-standard modal anatomy (Bootstrap modal-footer,
 *     Material mat-dialog-actions, headlessUI Dialog footer slot).
 *   - The footer is OUTSIDE the inner ``<form>``. Submit buttons
 *     inside the footer MUST carry ``form="<form-id>"`` so the
 *     HTML5 form-association links them back to the form (which
 *     stays inside ``.modal-content``).
 *   - Partials that don't ship a ``[data-modal-footer]`` continue
 *     to work unchanged — the helper just doesn't hoist anything
 *     and the partial keeps the legacy "footer-inside-body" layout.
 *
 * Required modal shell HTML (see ``consumable_list.html`` for
 * canonical example):
 *
 *   <div id="my-modal" class="hidden fixed inset-0 z-50 ...">
 *     <div class="flex items-center justify-center min-h-screen px-4">
 *       <div class="fixed inset-0 bg-gray-900/50 ..."></div>   {# dim #}
 *       <div class="relative bg-white ... flex flex-col">
 *         <div class="flex items-center justify-between px-6 py-4 border-b ...">
 *           <h3 class="modal-title">…</h3>
 *           <button class="modal-close-btn">…</button>
 *         </div>
 *         <div class="flex-1 overflow-y-auto p-6">
 *           <div class="modal-content">
 *             <!-- spinner placeholder; replaced by fetch.        -->
 *             <!-- Hoisted [data-modal-footer] (if any) lands as  -->
 *             <!-- a sibling of THIS scroll body, after it.       -->
 *           </div>
 *         </div>
 *       </div>
 *     </div>
 *   </div>
 */

import { lockButton, unlockButton } from '../forms/submit-button.js';
import { getCsrfToken } from '../csrf.js';

const SPINNER_HTML = `
  <div class="flex items-center justify-center py-12">
    <svg class="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  </div>`;

// Single document-level Escape listener for the SPA lifetime, in line
// with ``hx-boost-init.mdc``. Previously ``createDynamicModal`` added a
// new ``keydown`` listener on every call, which leaked one orphan
// listener per boosted navigation that re-instantiated a helper (the
// orphans were harmless because they closed over a detached ``modal``
// node that stayed ``.hidden``, but they accumulated forever).
//
// We track the currently open helper in ``activeModal``. Only one modal
// is ever visible at a time in the app (body scroll-lock + backdrop
// would conflict otherwise), so a single ref is enough; ``open()`` sets
// it, ``close()`` clears it.
let activeModal = null;
let escapeBound = false;

function bindEscapeOnce() {
  if (escapeBound) return;
  escapeBound = true;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (activeModal) activeModal.close();
  });
}

// Modal state is ephemeral UI and must NEVER survive history
// navigation (industry pattern: Stripe / Linear form dialogs — Back
// leaves the page, Forward never resurrects the dialog, let alone its
// stale 422 validation state). Two hooks, bound once for the SPA
// lifetime per hx-boost-init.mdc §1:
//
//   - htmx:beforeHistorySave — HTMX is about to snapshot #main-content
//     into its history cache. Close the active modal SYNCHRONOUSLY
//     (the regular close() waits out a 200 ms transition; the snapshot
//     is taken immediately after this event, so it would photograph
//     the modal mid-fade). The user is leaving the page — the abrupt
//     close is invisible.
//
//   - htmx:historyRestore — belt-and-braces for snapshots that predate
//     this hook (sessionStorage survives deploys within a tab). A
//     restored open shell is a ZOMBIE: innerHTML-only restore drops
//     every listener, so its submit would native-POST to the AJAX
//     endpoint and its close buttons are dead. Hide any such shell and
//     free the body scroll lock.
let historyHooksBound = false;

function sweepOpenShells() {
  // Scope to #main-content — the hx-history-elt snapshot boundary.
  // Shells living OUTSIDE it (global-confirm-modal, the assistant
  // slide-over) are never snapshotted and deliberately survive
  // navigation; they must not be touched here.
  const root = document.getElementById('main-content');
  if (!root) return;
  // A dynamic-modal shell is identifiable by its ``.modal-content``
  // child; the shell itself is the ``fixed inset-0`` wrapper (anatomy
  // documented in the module docstring above).
  let sweptAny = false;
  root.querySelectorAll('.modal-content').forEach((c) => {
    const shell = c.closest('div[id][class*="inset-0"]');
    if (shell && !shell.classList.contains('hidden')) {
      shell.classList.add('hidden');
      sweptAny = true;
    }
  });
  if (sweptAny) document.body.classList.remove('overflow-hidden');
}

function bindHistoryHooksOnce() {
  if (historyHooksBound) return;
  historyHooksBound = true;
  document.body.addEventListener('htmx:beforeHistorySave', () => {
    if (activeModal) activeModal.closeForSnapshot();
    // Also sweep shells no helper owns (e.g. a hand-built modal like
    // the customer-form location modal, or a shell whose page module
    // lost its helper reference).
    sweepOpenShells();
  });
  document.body.addEventListener('htmx:historyRestore', () => {
    sweepOpenShells();
  });
}

// Bind at module-evaluation time (single app bundle → runs once per
// tab), NOT inside createDynamicModal: pages with hand-built modals
// that never instantiate a helper still need the snapshot sweep.
bindHistoryHooksOnce();

/**
 * Create a dynamic modal helper bound to ``#${modalId}``.
 *
 * @param {object}   config
 * @param {string}   config.modalId             — id of the static modal shell.
 * @param {function} [config.onContentInjected] — fired after every content
 *     swap (initial GET and validation 422 re-render). Receives
 *     ``(contentEl, helper)``. Use this to re-init custom-selects,
 *     datepickers, TipTap, etc.
 * @param {function} [config.beforeSubmit]      — fired right before the
 *     POST. Receives ``(form, helper)``. Use this to sync widget state
 *     (e.g. TipTap HTML → hidden input) into the form data. May return
 *     ``false`` to abort the submission.
 * @param {function} [config.onSuccess]         — fired when the server
 *     returns ``{success: true}``. Default: close modal + soft reload.
 *     Receives ``(data, helper)``. Override to redirect, etc.
 * @param {function} [config.onClose]           — fired right after the
 *     modal is hidden (any close path). Receives ``(helper)``.
 * @param {string}   [config.errorLoadingMessage] — text shown if the
 *     initial GET fails.
 * @param {string}   [config.closeButtonText]   — text for the retry/close
 *     button on load failure.
 *
 * @returns {{open: function, close: function, modal: HTMLElement}|null}
 */
export function createDynamicModal(config) {
  const {
    modalId,
    onContentInjected = null,
    beforeSubmit = null,
    onSuccess = null,
    onClose = null,
    errorLoadingMessage = 'Error loading form. Please try again.',
    closeButtonText = 'Close',
  } = config || {};

  const modal = document.getElementById(modalId);
  if (!modal) return null;

  const content = modal.querySelector('.modal-content');
  const title = modal.querySelector('.modal-title');
  const staticCloseBtn = modal.querySelector('.modal-close-btn');

  // Find the backdrop (full-screen tinted layer) and the panel (the
  // bordered card containing ``.modal-content``). The shell HTML
  // documented in ``form-modals.mdc`` §1.a guarantees both exist:
  //   modal > .flex... > [backdrop fixed inset-0 bg-gray-900/50]
  //                     > [panel relative bg-white ... .modal-content]
  // We tolerate slight markup variations (max-width tier, dark colours)
  // because we look up by structure not exact class soup.
  const backdrop = modal.querySelector('div.fixed.inset-0[class*="bg-gray"]');
  const panel = content ? content.closest('div.relative') : null;

  // Apply transition classes once. Idempotent — if a future template
  // ever ships its own ``transition-*`` it stacks safely.
  const TRANSITION_MS = 200;
  if (backdrop) {
    backdrop.classList.add('transition-opacity', 'duration-200', 'ease-out');
  }
  if (panel) {
    panel.classList.add(
      'transition-all', 'duration-200', 'ease-out',
      'transform-gpu', 'will-change-transform',
    );
  }

  // Closed-state classes (the "from" of the transition). Kept tiny so
  // the modal feels lively but never travels far enough to look
  // disconnected from the trigger.
  const BACKDROP_CLOSED = ['opacity-0'];
  const PANEL_CLOSED = ['opacity-0', 'scale-95'];

  let currentUrl = null;
  let closingTimer = null;

  function applyClosedState() {
    if (backdrop) backdrop.classList.add(...BACKDROP_CLOSED);
    if (panel) panel.classList.add(...PANEL_CLOSED);
  }

  function applyOpenState() {
    if (backdrop) backdrop.classList.remove(...BACKDROP_CLOSED);
    if (panel) panel.classList.remove(...PANEL_CLOSED);
  }

  function open(url, titleText) {
    if (closingTimer) {
      clearTimeout(closingTimer);
      closingTimer = null;
    }

    activeModal = helper;

    currentUrl = url;
    if (title && titleText) title.textContent = titleText;
    if (content) content.innerHTML = SPINNER_HTML;

    // 1. Pre-paint in the "closed" state so the entrance transition has
    //    a starting point. We must do this BEFORE removing ``.hidden``
    //    so the browser never composites the modal at full opacity.
    applyClosedState();

    // 2. Reveal the shell.
    modal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');

    // 3. On the next two animation frames, swap to the "open" state —
    //    this guarantees the browser flushes the closed-state styles
    //    first and then runs the transition. ``requestAnimationFrame``
    //    once is sometimes too eager (Chrome will batch both classes
    //    in a single frame and skip the transition); two frames is the
    //    canonical fix.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyOpenState();
      });
    });

    loadContent();
  }

  /**
   * Close the modal.
   *
   * @param {function} [afterClose] — optional callback invoked once the
   *   leave transition is fully done and the shell is hidden. Use this
   *   for any side effect that visually disturbs the page (HTMX swap,
   *   navigation, ``window._softReload()``, ``window.location.href``
   *   reassignment, …) — running it inline alongside the call to
   *   ``close()`` tears down the backdrop element mid-fade and the
   *   user perceives a "white flash" (Pattern B response is faster
   *   than the 200 ms transition).
   */
  function close(afterClose) {
    if (closingTimer) {
      clearTimeout(closingTimer);
      closingTimer = null;
    }

    // Release the Escape ownership immediately — a second Escape during
    // the leave transition must not retrigger ``close()``.
    if (activeModal === helper) activeModal = null;

    // 1. Trigger the leave transition.
    applyClosedState();

    // 2. After the transition ends, hide the shell, free body scroll,
    //    reset content. We use a setTimeout fallback in case the
    //    backdrop / panel are missing or ``transitionend`` never fires
    //    (offscreen, display:none, prefers-reduced-motion, …).
    closingTimer = setTimeout(() => {
      closingTimer = null;
      modal.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
      currentUrl = null;
      if (content) content.innerHTML = SPINNER_HTML;
      // Strip the hoisted footer too — content.innerHTML reset only
      // clears the body. Left in place, the panel would render
      // « spinner + stale footer » on the next open() call, which
      // looks broken.
      if (panel) {
        panel
          .querySelectorAll(':scope > [data-modal-footer]')
          .forEach((n) => n.remove());
      }
      if (typeof onClose === 'function') {
        try { onClose(helper); } catch (e) { console.error(e); }
      }
      if (typeof afterClose === 'function') {
        try { afterClose(helper); } catch (e) { console.error(e); }
      }
    }, TRANSITION_MS);
  }

  /**
   * Synchronous close used by the htmx:beforeHistorySave hook: same
   * teardown as ``close()`` but with zero transition and zero timers,
   * so the DOM is already in its resting (hidden, spinner-reset)
   * state when HTMX snapshots the page a tick later.
   */
  function closeForSnapshot() {
    if (closingTimer) {
      clearTimeout(closingTimer);
      closingTimer = null;
    }
    if (activeModal === helper) activeModal = null;
    applyClosedState();
    modal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    currentUrl = null;
    if (content) content.innerHTML = SPINNER_HTML;
    if (panel) {
      panel
        .querySelectorAll(':scope > [data-modal-footer], :scope > .modal-footer-slot')
        .forEach((n) => n.remove());
    }
    if (typeof onClose === 'function') {
      try { onClose(helper); } catch (e) { console.error(e); }
    }
  }

  function loadContent() {
    if (!content || !currentUrl) return;

    fetch(currentUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        injectContent(html);
      })
      .catch((error) => {
        console.error('[dynamic-modal] load failed:', error);
        content.innerHTML = `
          <div class="text-center py-12">
            <p class="text-red-600 dark:text-red-400">${errorLoadingMessage}</p>
            <button type="button" class="mt-4 btn-secondary" data-modal-close>${closeButtonText}</button>
          </div>`;
        bindInnerCloseTargets();
      });
  }

  // PO 2026-05-21 — sticky footer slot. The injected partial may
  // ship a ``[data-modal-footer]`` block that the helper hoists out
  // of ``.modal-content`` and parks as a direct sibling of the
  // scroll body. The panel's ``flex flex-col`` layout then renders
  // the three slots (header + body + footer) with the footer pinned
  // at the bottom — always visible, identical anatomy to the
  // hand-built location-modal in ``_form_content.html``.
  //
  // ``hoistFooter`` is called from ``injectContent`` (both the
  // initial GET and every 422 re-render) and from ``close`` (to
  // wipe stale footer markup before the next open).
  function hoistFooter() {
    if (!panel) return;

    // Strip any previously-hoisted footer first. Targets both
    // ``data-modal-footer`` (canonical) and the helper class we
    // also stamp below, so leftover markup from a partial that
    // drops the attribute mid-edit still gets cleaned up.
    panel
      .querySelectorAll(':scope > [data-modal-footer], :scope > .modal-footer-slot')
      .forEach((n) => n.remove());

    if (!content) return;
    const footer = content.querySelector('[data-modal-footer]');
    if (!footer) return;

    // Move (not clone) the footer node. Anything bound to the
    // footer subtree (event listeners, etc.) survives the
    // re-parenting since we don't touch innerHTML on it.
    panel.appendChild(footer);
  }

  function injectContent(html) {
    content.innerHTML = html;

    // Re-create <script> elements so the browser actually executes
    // them (innerHTML does NOT execute scripts per the HTML5 spec).
    //
    // Two important nuances:
    //
    // 1. Skip non-executable scripts (e.g. ``<script type="application/json"
    //    id="absence-form-config">``, the standard Django ``json_script``
    //    output). Re-creating these as bare ``<script>`` strips the
    //    ``type`` attribute and the browser then tries to parse them as
    //    JS, blowing up on the first ``:`` of the JSON payload
    //    (regression observed on the absence form modal, Phase 17).
    //    json_script blocks are already in the DOM after innerHTML —
    //    nothing to re-execute, just leave them alone.
    //
    // 2. Preserve ``type`` and ``id`` on JS scripts too, so an
    //    inline module + a sibling json_script can coexist in the
    //    same modal body without surprises.
    function isExecutableType(t) {
      if (!t) return true;
      const lower = String(t).trim().toLowerCase();
      return (
        lower === ''
        || lower === 'text/javascript'
        || lower === 'application/javascript'
        || lower === 'application/ecmascript'
        || lower === 'text/ecmascript'
        || lower === 'module'
      );
    }

    content.querySelectorAll('script').forEach((oldScript) => {
      if (!isExecutableType(oldScript.getAttribute('type'))) {
        return;
      }
      const newScript = document.createElement('script');
      const t = oldScript.getAttribute('type');
      if (t) newScript.type = t;
      if (oldScript.id) newScript.id = oldScript.id;
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Hoist BEFORE we bind the form's submit listener — the form
    // stays in content but its companion footer (with the submit
    // button linked via ``form="<form-id>"``) moves into the
    // panel. After hoisting the footer is a sibling of the scroll
    // body, so ``bindInnerCloseTargets`` below must look in both
    // ``content`` AND the hoisted footer to wire the Cancel button.
    hoistFooter();

    const form = content.querySelector('form');
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    }

    bindInnerCloseTargets();

    if (typeof onContentInjected === 'function') {
      try { onContentInjected(content, helper); } catch (e) { console.error(e); }
    }
  }

  function bindInnerCloseTargets() {
    // Close buttons can live inside the form (legacy partials) OR
    // inside the hoisted ``[data-modal-footer]`` (canonical pattern
    // since PO 2026-05-21). Scan both so neither layout breaks.
    content.querySelectorAll('[data-modal-close]').forEach((btn) => {
      btn.addEventListener('click', close);
    });
    if (panel) {
      panel
        .querySelectorAll(':scope > [data-modal-footer] [data-modal-close]')
        .forEach((btn) => btn.addEventListener('click', close));
    }
  }

  function handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;

    if (typeof beforeSubmit === 'function') {
      try {
        if (beforeSubmit(form, helper) === false) return;
      } catch (e) {
        console.error('[dynamic-modal] beforeSubmit threw:', e);
        return;
      }
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) lockButton(submitBtn);

    fetch(form.action, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRFToken': getCsrfToken() || '',
      },
      body: new FormData(form),
      credentials: 'same-origin',
    })
      .then((response) => {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          return response.json().then((data) => ({ kind: 'json', data, ok: response.ok }));
        }
        return response.text().then((text) => ({ kind: 'html', data: text, ok: response.ok }));
      })
      .then((result) => {
        if (result.kind === 'json') {
          if (result.ok && result.data && result.data.success) {
            if (typeof onSuccess === 'function') {
              onSuccess(result.data, helper);
            } else {
              // Defer the soft-reload until the close transition has
              // fully completed. The modal lives inside #main-content
              // (see form-modals.mdc §1.a), so any HTMX swap of
              // #main-content destroys the backdrop element. If the
              // swap arrives mid-transition the backdrop disappears in
              // a single frame and the user perceives a "white flash".
              // Pattern A pages masked this race accidentally because
              // their full-page response took longer than the 200 ms
              // close transition; Pattern B partials are typically
              // <100 ms and consistently beat the transition.
              close(() => {
                if (typeof window._softReload === 'function') window._softReload();
              });
            }
          } else {
            console.error('[dynamic-modal] unexpected JSON response:', result);
          }
          return;
        }
        injectContent(result.data);
      })
      .catch((error) => {
        console.error('[dynamic-modal] submit failed:', error);
        if (typeof window.showToast === 'function') {
          window.showToast(errorLoadingMessage, 'error');
        }
      })
      .finally(() => {
        if (submitBtn) unlockButton(submitBtn);
      });
  }

  if (staticCloseBtn) {
    staticCloseBtn.addEventListener('click', close);
  }

  bindEscapeOnce();

  const helper = { open, close, closeForSnapshot, modal };
  return helper;
}
